import { ClientTask } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusIcon, Pencil, Trash2, CheckSquare, Square, Calendar } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type TasksPanelProps = {
  tasks: ClientTask[];
  clientId: number;
};

export function TasksPanel({ tasks, clientId }: TasksPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const addTask = useMutation({
    mutationFn: async (payload: any) => apiRequest('POST', '/api/client-tasks', { clientId, ...payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/tasks`] });
      setIsAddDialogOpen(false);
      toast({ title: 'Task added' });
    },
    onError: () => toast({ title: 'Failed to add task', variant: 'destructive' })
  });
  const updateTask = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: any }) => apiRequest('PATCH', `/api/client-tasks/${id}`, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/tasks`] });
      setIsEditDialogOpen(false);
      setSelectedTaskId(null);
      toast({ title: 'Task updated' });
    },
    onError: () => toast({ title: 'Failed to update task', variant: 'destructive' })
  });
  const deleteTask = useMutation({
    mutationFn: async (id: number) => apiRequest('DELETE', `/api/client-tasks/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/tasks`] });
      setIsDeleteDialogOpen(false);
      setSelectedTaskId(null);
      toast({ title: 'Task deleted' });
    },
    onError: () => toast({ title: 'Failed to delete task', variant: 'destructive' })
  });
  
  const selectedTask = selectedTaskId 
    ? tasks.find(task => task.id === selectedTaskId) 
    : null;
    
  // Sort tasks: incomplete first (sorted by due date), then completed
  const sortedTasks = [...tasks].sort((a, b) => {
    // First, sort by completion status
    if (!a.completedAt && b.completedAt) return -1;
    if (a.completedAt && !b.completedAt) return 1;
    
    // If both are incomplete, sort by due date (if available)
    if (!a.completedAt && !b.completedAt) {
      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      }
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
    }
    
    // For completed tasks, sort by completion date (most recent first)
    if (a.completedAt && b.completedAt) {
      return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
    }
    
    // Default sort by creation date
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  function handleAddTask() {
    setIsAddDialogOpen(true);
  }

  function handleEditTask(task: ClientTask) {
    setSelectedTaskId(task.id);
    setIsEditDialogOpen(true);
  }

  function handleDeleteTask(task: ClientTask) {
    setSelectedTaskId(task.id);
    setIsDeleteDialogOpen(true);
  }
  
  function handleToggleTask(task: ClientTask) {
    const newCompleted = !!task.completedAt ? null : new Date().toISOString();
    updateTask.mutate({ id: task.id!, updates: { completedAt: newCompleted, status: newCompleted ? 'completed' : 'to do' } });
  }
  
  function getPriorityBadge(priority: string | null) {
    if (!priority) return null;
    
    switch (priority.toLowerCase()) {
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'medium':
        return <Badge variant="secondary">Medium</Badge>;
      case 'low':
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  }
  
  function getStatusBadge(status: string) {
    switch (status.toLowerCase()) {
      case 'todo':
      case 'to do':
      case 'not started':
        return <Badge variant="outline">To Do</Badge>;
      case 'in progress':
        return <Badge variant="secondary">In Progress</Badge>;
      case 'completed':
        return <Badge variant="default">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Tasks</h3>
        <Button size="sm" onClick={handleAddTask}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Task
        </Button>
      </div>

      {tasks.length === 0 ? (
        <Alert>
          <AlertDescription>
            No tasks have been created for this contact yet.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {sortedTasks.map((task) => (
            <Card key={task.id} className={cn(
              "overflow-hidden",
              task.completedAt && "opacity-60"
            )}>
              <CardContent className="p-0">
                <div className="p-4 flex">
                  <div 
                    className="flex-shrink-0 mt-0.5 cursor-pointer"
                    onClick={() => handleToggleTask(task)}
                  >
                    {task.completedAt ? (
                      <CheckSquare className="h-5 w-5 text-primary" />
                    ) : (
                      <Square className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  
                  <div className="ml-3 flex-1">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h4 className={cn(
                          "font-medium",
                          task.completedAt && "line-through"
                        )}>
                          {task.title}
                        </h4>
                        
                        <div className="flex flex-wrap gap-2 items-center text-sm text-muted-foreground">
                          {getStatusBadge(task.status)}
                          {task.priority && getPriorityBadge(task.priority)}
                          
                          {task.dueDate && (
                            <div className={cn(
                              "flex items-center gap-1",
                              !task.completedAt && new Date(task.dueDate) < new Date() && "text-destructive"
                            )}>
                              <Calendar className="h-3.5 w-3.5" />
                              <span>Due: {format(new Date(task.dueDate), 'MMM d, yyyy')}</span>
                              {!task.completedAt && new Date(task.dueDate) < new Date() && " (Overdue)"}
                            </div>
                          )}
                          
                          {task.assignedTo && (
                            <span>Assigned to: {task.assignedTo}</span>
                          )}
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
                              <path d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                            </svg>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleToggleTask(task)}>
                            {task.completedAt ? (
                              <>
                                <Square className="mr-2 h-4 w-4" />
                                <span>Mark as Incomplete</span>
                              </>
                            ) : (
                              <>
                                <CheckSquare className="mr-2 h-4 w-4" />
                                <span>Mark as Complete</span>
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditTask(task)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            <span>Edit</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteTask(task)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            <span>Delete</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {task.description && (
                      <p className="mt-2 text-sm">{task.description}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Task Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>
              Create a new task for this contact.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Form will be implemented here to add new tasks.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Update this task's details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Form will be implemented here to edit tasks.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Task Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTask && (
            <div className="py-4 border rounded-md p-3 bg-muted/50">
              <p className="font-medium">{selectedTask.title}</p>
              {selectedTask.description && (
                <p className="text-sm text-muted-foreground truncate">
                  {selectedTask.description}
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
